import { ChevronRightIcon } from "@radix-ui/react-icons";
import { Card, ScatterChart, Text, Title } from "@tremor/react";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useGetProjectProgressAndDates } from "src/api/Analytics/project";
import { ProjectsV2FilterGroup } from "src/api/Services/Projects/projects";
import { useGetTaskersForInstance } from "src/api/Services/Tasks/tasks";
import { useGetSpaces } from "src/api/Spaces/spaces";
import { Loading } from "src/components/ui/loading";
import { clean, Filter, StatusFilter } from "src/pages/spaces/space/projects";
import { getProjectProgressAndDatesFilterKey } from "src/utils/filters";

type Props = {};

type ProjectData = {
  _id: string;
  name: string;
  startDate?: string;
  createdAt: string;
  closedTasks: number;
  totalTasks: number;
};

export default function ProjectTimeVsProgress({}: Props) {
  const [filterGroup, setFilterGroup] = useState<ProjectsV2FilterGroup>(() => {
    const savedFilterGroup = localStorage.getItem(
      getProjectProgressAndDatesFilterKey()
    );
    if (savedFilterGroup) {
      return JSON.parse(savedFilterGroup);
    }
    return {
      logic: "AND",
      children: [
        {
          field: "project-status",
          comparator: "in",
          value: ["In Progress"],
        },
      ],
    };
  });
  useEffect(() => {
    if (filterGroup.children.length === 0) {
      localStorage.removeItem(getProjectProgressAndDatesFilterKey());
    } else {
      localStorage.setItem(
        getProjectProgressAndDatesFilterKey(),
        JSON.stringify(filterGroup)
      );
    }
  }, [filterGroup]);
  const { data: instanceMembers } = useGetTaskersForInstance();
  const { data: spaces } = useGetSpaces();

  const { data: projects } = useGetProjectProgressAndDates(clean(filterGroup));
  const chartdata = useMemo(
    () =>
      projects?.map((item) => {
        const progress = (item.closedTasks / item.totalTasks) * 100;
        const days = item.assignedStartDate
          ? Math.abs(
              dayjs(
                dayjs(item.assignedStartDate).isBefore(dayjs())
                  ? item.assignedStartDate
                  : item.createdAt
              ).diff(dayjs(), "days")
            )
          : Math.abs(dayjs(item.createdAt).diff(dayjs(), "days"));

        // Add a status field to determine color
        const status =
          progress >= 10 && progress <= 50 && days > 30 ? "at-risk" : "normal";

        return {
          _id: item._id,
          Progress: progress,
          Project: item.name,
          Days: days,
          status: status, // Add this field for coloring
        };
      }) ?? [],
    [projects]
  );

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-12 items-center border-b border-gray-200/70 px-8 text-sm text-gray-800">
        <div className="flex items-center space-x-2 text-sm">
          <Link to={`/analytics`}>
            <div className="text-gray-800">{"Analytics"}</div>
          </Link>
          <ChevronRightIcon className="h-3 w-3 text-gray-500" />
          <div className="cursor-default text-gray-800">
            {"Project Progress vs Duration"}
          </div>
        </div>
      </div>
      <div className="flex h-14 items-center gap-2 border-b border-gray-200/70 px-8">
        {/* <Filter
            filterGroup={filterGroup}
            setFilterGroup={setFilterGroup}
            isLoading={!projectOwners}
            // localStorageKey="project-filter"
            filterModels={[
              {
                field: "project-owner",
                projectOwners:
                  // Array.from(
                  //   new Map(
                  //     projects?.projects.map((p) => [
                  //       p.owner._id,
                  //       {
                  //         _id: p.owner._id,
                  //         name: p.owner.name,
                  //         email: p.owner.email,
                  //       },
                  //     ])
                  //   ).values()
                  // ) ?? [],
                  projectOwners?.map((m) => {
                    return {
                      _id: m._id,
                      name: m.name,
                      email: m.email,
                    };
                  }) ?? [],
              },
              { field: "project-status" },
              { field: "company" },
              {
                field: "project-template",
                projectTemplates:
                  journeyTemplates
                    ?.filter((jt) => jt.spaceId?.toString() === spaceId)
                    .map((jt) => {
                      return {
                        _id: jt._id?.toString() ?? "",
                        name: jt.name,
                      };
                    }) ?? [],
              },
            ]}
          /> */}
        <Filter
          filterGroup={filterGroup}
          setFilterGroup={setFilterGroup}
          isLoading={!instanceMembers}
          // localStorageKey="project-filter"
          filterModels={[
            {
              field: "project-owner",
              projectOwners:
                // Array.from(
                //   new Map(
                //     projects?.projects.map((p) => [
                //       p.owner._id,
                //       {
                //         _id: p.owner._id,
                //         name: p.owner.name,
                //         email: p.owner.email,
                //       },
                //     ])
                //   ).values()
                // ) ?? [],
                instanceMembers?.map((m) => {
                  return {
                    _id: m._id,
                    name: m.name,
                    email: m.email,
                  };
                }) ?? [],
            },
            // { field: "project-status" },
            { field: "company" },
            {
              field: "space",
              spaces:
                spaces
                  // ?.filter((s) => s.settings.timeTracking)
                  ?.map((s) => ({
                    _id: s._id,
                    name: s.name,
                    color: s.color,
                  })) ?? [],
            },
            {
              field: "project-start-date",
            },
            {
              field: "project-progress",
            },
            // {
            //   field: "project-template",
            //   projectTemplates:
            //     journeyTemplates
            //       ?.filter((jt) => jt.spaceId?.toString() === spaceId)
            //       .map((jt) => {
            //         return {
            //           _id: jt._id?.toString() ?? "",
            //           name: jt.name,
            //         };
            //       }) ?? [],
            // },
          ]}
        />
        <StatusFilter
          filterGroup={filterGroup}
          setFilterGroup={setFilterGroup}
        />
      </div>
      <div className="flex flex-1 flex-col overflow-auto px-8 py-6">
        {!!projects ? (
          <Card className="w-full max-w-5xl p-6 py-4 pb-6">
            <Title>Progress vs Duration</Title>
            <Text>
              Identify projects that have not made enough progress for the
              duration they have been active
            </Text>
            <div className="relative pb-4">
              <div className="relative flex items-center pl-12">
                <div className="absolute left-0 top-1/2 -translate-y-1/2 -rotate-90 text-sm">
                  Progress
                </div>
                <ScatterChart
                  className="mt-4 h-96 py-4"
                  data={chartdata}
                  category="Project"
                  y="Progress"
                  x="Days"
                  showOpacity={true}
                  colors={["purple"]}
                  minYValue={0}
                  maxYValue={100}
                  showAnimation
                  showGridLines
                  valueFormatter={{
                    y: (progress) => `${progress.toFixed(0)}%`,
                    x: (days) => `${days} days`,
                  }}
                  // enableLegendSlider
                  showLegend={false}
                  intervalType="preserveStartEnd"
                  noDataText="No projects meet your filter criteria"
                />
              </div>
              <div className="absolute left-1/2 bottom-1 -translate-x-1/2 pl-12">
                <span className="text-sm">Duration</span>
              </div>
            </div>
          </Card>
        ) : (
          <Loading className="mx-auto mt-[20vh]" />
        )}
      </div>
    </div>
  );
}
